/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    type ReactElement,
    type PropsWithChildren,
} from 'react';

export type InsiderModalSection = 'login' | 'register';

interface ModalContextType {
    /** Insider modal (join/sign-in) */
    insiderModalOpen: boolean;
    insiderModalSection: InsiderModalSection;
    /** Return URL passed to the login/register panels so a successful auth redirects there instead of /account */
    insiderModalReturnUrl: string;
    openInsiderModal: (section: InsiderModalSection, returnUrl?: string) => void;
    closeInsiderModal: () => void;

    /**
     * True between step 1 (register) succeeding and step 2 (details) completing.
     * Persists across modal close/reopen — cleared only when step 2 explicitly
     * completes. Used to suppress the green dot and footer logged-in banner until
     * registration is fully complete.
     */
    registrationPending: boolean;
    markRegistrationPending: () => void;
    clearRegistrationPending: () => void;

    /** SMS/Email signup modal */
    smsModalOpen: boolean;
    openSmsModal: () => void;
    closeSmsModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: PropsWithChildren): ReactElement {
    const [insiderModalOpen, setInsiderModalOpen] = useState(false);
    const [insiderModalSection, setInsiderModalSection] = useState<InsiderModalSection>('login');
    const [insiderModalReturnUrl, setInsiderModalReturnUrl] = useState('');
    const [registrationPending, setRegistrationPending] = useState(false);
    const [smsModalOpen, setSmsModalOpen] = useState(false);

    const openInsiderModal = useCallback((section: InsiderModalSection, returnUrl?: string) => {
        setInsiderModalSection(section);
        setInsiderModalReturnUrl(returnUrl ?? '');
        setInsiderModalOpen(true);
    }, []);

    const closeInsiderModal = useCallback(() => {
        setInsiderModalOpen(false);
        setInsiderModalSection('login');
        setInsiderModalReturnUrl('');
        // registrationPending intentionally NOT cleared here — it must survive
        // modal close so the UI stays in guest mode if step 2 is unfinished.
    }, []);

    const markRegistrationPending = useCallback(() => setRegistrationPending(true), []);
    // Only called when step 2 explicitly completes.
    const clearRegistrationPending = useCallback(() => setRegistrationPending(false), []);

    const openSmsModal = useCallback(() => {
        setSmsModalOpen(true);
    }, []);

    const closeSmsModal = useCallback(() => {
        setSmsModalOpen(false);
    }, []);

    const value = useMemo<ModalContextType>(
        () => ({
            insiderModalOpen,
            insiderModalSection,
            insiderModalReturnUrl,
            openInsiderModal,
            closeInsiderModal,
            registrationPending,
            markRegistrationPending,
            clearRegistrationPending,
            smsModalOpen,
            openSmsModal,
            closeSmsModal,
        }),
        [
            insiderModalOpen,
            insiderModalSection,
            insiderModalReturnUrl,
            openInsiderModal,
            closeInsiderModal,
            registrationPending,
            markRegistrationPending,
            clearRegistrationPending,
            smsModalOpen,
            openSmsModal,
            closeSmsModal,
        ]
    );

    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}

export function useModalContext(): ModalContextType {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModalContext must be used within a ModalProvider');
    }
    return context;
}
